import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { XMarkIcon } from '@heroicons/react/24/outline';

const AddEquipmentModal = ({ open, onClose, onSubmit, submitButtonText = 'Add Equipment' }) => {
  const [equipmentName, setEquipmentName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const resetForm = () => {
    setEquipmentName('');
    setDescription('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!equipmentName.trim()) {
      setError('Equipment name is required');
      return;
    }

    if (equipmentName.trim().length < 2) {
      setError('Equipment name must be at least 2 characters long');
      return;
    }

    onSubmit({
      name: equipmentName.trim(),
      description: description.trim()
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />
      <div className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">
            Add New Equipment
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="equipmentName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Equipment Name <span className="text-red-500">*</span>
            </label>
            <input
              ref={inputRef}
              type="text"
              id="equipmentName"
              value={equipmentName}
              onChange={(e) => {
                setEquipmentName(e.target.value);
                setError('');
              }}
              placeholder="Enter equipment name"
              maxLength={50}
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:border-shoorah-primary dark:text-white"
            />
            {error ? <p className="text-red-500 text-sm mt-1">{error}</p> : null}
            <p className="text-gray-500 text-sm mt-1">{equipmentName.length}/50 characters</p>
          </div>

          <div className="mb-4">
            <label
              htmlFor="equipmentDescription"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Description
            </label>
            <textarea
              id="equipmentDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter equipment description"
              rows={4}
              maxLength={500}
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:border-shoorah-primary dark:text-white"
            />
            <p className="text-gray-500 text-sm mt-1">{description.length}/500 characters</p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!equipmentName.trim()}
              className="btn-primary flex-1"
            >
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AddEquipmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitButtonText: PropTypes.string
};

export default AddEquipmentModal;
